import { getCurrentDayInTimezone, getCurrentMonthInTimezone, getMonthAccordingToBirthdayDay, getMonthRange, getTargetYear } from "../../utils/dateFromat";
import { CategoryType, JaiminiType, MDADPredictionType, MonthEnum, PadithiEnum, PredictionType } from "../../utils/enums";

import moment from "moment-timezone";

export const createGetPredictionPayload = (filter) => {
  const { conditionType, predictionType, category, month, day } = filter;

  let payload = {
    predictionType: predictionType,
    category,
  };

  if (conditionType !== PadithiEnum.MASTER) payload.conditionType = conditionType

  if (predictionType === MDADPredictionType.MD) payload.predictionType = PredictionType.YEAR
  if (predictionType === MDADPredictionType.AD) payload.predictionType = PredictionType.MONTH

  if (predictionType === PredictionType.MONTH || predictionType === MDADPredictionType.AD) payload.month = month

  if (predictionType === PredictionType.DAY) {
    payload = {
      ...payload,
      month: month,
      day: day,
      category: CategoryType.DAY
    }
  }
  return payload;
};

export const getPredictionLevel = ({ conditionType, category, predictionType }) => {
  const handlers = {
    [PadithiEnum.LAL_KITAB]: getLalKitabPredictionLevel,
  };

  const handler = handlers[conditionType];
  return handler
    ? handler({ category, predictionType })
    : getDefaultPredictionLevel(predictionType);
};

function getDefaultPredictionLevel(predictionType) {
  switch (predictionType) {
    case PredictionType.YEAR:
    case MDADPredictionType.MD:
      return "Yearly Predictions";

    case PredictionType.MONTH:
    case MDADPredictionType.AD:
      return "Monthly Predictions";
    case PredictionType.DAY:
      return "Day";
    default:
      return "";
  }
}

function getLalKitabPredictionLevel({ category, predictionType }) {
  if (category === CategoryType.PROMISE && predictionType === PredictionType.YEAR) return "";

  switch (predictionType) {
    case PredictionType.YEAR:
      return "Yearly Predictions";
    case PredictionType.MONTH:
      return "Monthly Predictions";
    case PredictionType.DAY:
      return "Day";
    default:
      return "Yearly Predictions";
  }
}

export function createPredictionInterval({
  birthDate,
  birthTZ,
  currentTZ,
  intervalType,
  month,
  day,
}) {
  if (!birthDate) return null;

  const birth = moment.tz(birthDate, birthTZ);
  const birthMonth = birth.month() + 1;
  const birthDay = birth.date();

  const year = getTargetYear({ birthDate, birthTZ, currentTZ, targetMonth: month, targetDay: day })

  switch (intervalType) {
    // ---------------------- YEARLY ----------------------
    case "yearly": {
      const start = moment.tz(
        `${year - 1}-${birthMonth}-${birthDay}`,
        currentTZ
      );

      const end = start.clone().add(1, "year").subtract(1, "day");

      return { start: start.toISOString(), end: end.toISOString() };
    }

    // ---------------------- MONTHLY ----------------------
    case "monthly": {
      const selectedMonth = month ?? (moment.tz(currentTZ).month() + 1);

      const start = moment.tz(
        `${year}-${selectedMonth}-${birthDay}`,
        currentTZ
      );

      const end = start.clone().add(1, "month").subtract(1, "day");

      return { start: start.toISOString(), end: end.toISOString() };
    }

    // ---------------------- DAILY ----------------------
    case "daily": {
      if (!month || !day) return null;

      const start = moment.tz(`${year}-${month}-${day}`, currentTZ);
      const end = start.clone().add(1, "day");

      return { start: start.toISOString(), end: end.toISOString() };
    }

    default:
      return null;
  }
}

function getPredictionYearRange({ birthDate, birthTZ, timezone }) {

  const now = moment().tz(timezone);

  const bday = moment.tz(birthDate, birthTZ);

  const bMonth = bday.month() + 1;
  const bDay = bday.date();

  const thisYearBirthday = moment.tz(
    `${now.year()}-${bMonth}-${bDay}`,
    birthTZ
  );

  let start, end;

  if (now.isBefore(thisYearBirthday, "day")) {
    start = moment(thisYearBirthday).subtract(1, "year");
    end = thisYearBirthday;
  } else {
    start = thisYearBirthday;
    end = moment(thisYearBirthday).add(1, "year");
  }

  const finalEnd = moment(end).subtract(1, "day");

  return {
    startDate: start.toDate(),
    endDate: finalEnd.toDate(),
    startDateFormatted: start.format("DD MMM YYYY"),
    endDateFormatted: finalEnd.format("DD MMM YYYY")
  };
}

function getPredictionMonthRange({
  birthDate,
  birthTZ,
  timezone,
  month,
}) {
  if (!month || month === MonthEnum.CURRENT_MONTH) {
    month = getMonthAccordingToBirthdayDay(birthDate, timezone, birthTZ);
  }

  const bday = moment.tz(birthDate, birthTZ);
  const birthDay = bday.date();

  const year = getTargetYear({ birthDate, birthTZ, currentTZ: timezone, targetMonth: month });

  // -----------------------------------------
  // 1. START DATE
  // -----------------------------------------
  let start = moment.tz(
    { year, month: month - 1, day: birthDay },
    timezone
  );

  let startFallbackUsed = false;

  if (!start.isValid() || start.month() !== month - 1) {
    start = moment
      .tz({ year, month: month - 1, day: 1 }, timezone)
      .endOf("month");

    startFallbackUsed = true;
  }

  // -----------------------------------------
  // 2. END DATE
  // -----------------------------------------
  let nextMonth = start.clone().add(1, "month");
  let end = nextMonth.clone().date(birthDay);

  let endFallbackUsed = false;

  // If invalid (e.g., birthday=31 in a short month)
  if (!end.isValid() || end.month() !== nextMonth.month()) {
    endFallbackUsed = true;

    // birthday = 1 → same month end-of-month
    if (birthDay === 1) {
      end = start.clone().endOf("month");
    } else {
      end = nextMonth.clone().endOf("month");
    }
  } else {
    // Normal case subtract 1 day
    end = end.subtract(1, "day");
  }

  // -----------------------------------------
  // SPECIAL CASE: birthday 31 fallback → we want SAME last day (not minus 1)
  // -----------------------------------------
  if (birthDay === 31 && startFallbackUsed && endFallbackUsed) {
    // Overwrite: end should be the last day of next month
    end = nextMonth.clone().endOf("month");
  }

  return {
    startDate: start.toDate(),
    endDate: end.toDate(),
    startDateFormatted: start.format("DD MMM YYYY"),
    endDateFormatted: end.format("DD MMM YYYY"),
  };
}

function getPredictionDayRange({
  birthDate,
  birthTZ,
  timezone,
  month,
  day,
}) {
  if (!month) {
    month = getCurrentMonthInTimezone(timezone);
  }
  if (!day) {
    day = getCurrentDayInTimezone(timezone);
  }
  const year = getTargetYear({ birthDate, birthTZ, currentTZ: timezone, targetMonth: month, targetDay: day });

  const start = moment(`${year}-${month}-${day}`);
  const end = moment(start).add(1, "day").subtract(1, "second");

  return {
    startDate: start.toDate(),
    endDate: end.toDate(),
    startDateFormatted: start.format("DD MMM YYYY"),
    endDateFormatted: end.format("DD MMM YYYY"),
  };
}

export function getIntervalByPredictionType({
  predictionType,
  birthDate,
  birthTZ,
  currentTZ,
  month,
  day,
}) {

  switch (predictionType) {
    case PredictionType.YEAR:
      return getPredictionYearRange({ birthDate, birthTZ, timezone: currentTZ });

    case PredictionType.MONTH:
      return getPredictionMonthRange({
        birthDate,
        birthTZ,
        timezone: currentTZ,
        month,
      })

    case PredictionType.DAY:
      return getPredictionDayRange({
        birthDate,
        birthTZ,
        timezone: currentTZ,
        month,
        day
      })
    default:
      return getPredictionYearRange({ birthDate, birthTZ: birthTZ, timezone: currentTZ });

  }

}


export const hasAnyPrediction = (data) => {
  if (!data) return false;
  return Object.values(data).some(Boolean);
};

export const getSelectedPrediction = (data, conditionType) => {
  if (!data) return null;

  switch (conditionType) {
    case PadithiEnum.LAL_KITAB:
      return data?.[PadithiEnum.LAL_KITAB];
    case PadithiEnum.NADI:
      return data?.[PadithiEnum.NADI];;
    case PadithiEnum.PARASHARA:
      return data?.[PadithiEnum.PARASHARA];
    case PadithiEnum.JAIMINI:
      return data?.[PadithiEnum.JAIMINI];
    default:
      return null;
  }
}
function getDateRange({
  predictionType,
  birthDate,
  birthTimezone,
  userCurrentTimezone,
  targetMonth,
  targetDay,
}) {
  const birthDT = moment.tz(birthDate, birthTimezone);
  const birthDay = birthDT.date();
  const birthMonth = birthDT.month() + 1; // moment months are 0-based

  const now = moment.tz(userCurrentTimezone);
  const currentYear = now.year();

  switch (predictionType) {
    case PredictionType.YEAR:
    case MDADPredictionType.MD: {
      let startYear = currentYear;
      let endYear = currentYear + 1;

      const thisYearBirthday = moment.tz(
        {
          year: currentYear,
          month: birthMonth - 1,
          day: birthDay,
        },
        userCurrentTimezone
      );

      if (now.isBefore(thisYearBirthday)) {
        startYear = currentYear - 1;
        endYear = currentYear;
      }

      const start = moment.tz(
        {
          year: startYear,
          month: birthMonth - 1,
          day: birthDay,
          hour: 0,
          minute: 0,
          second: 0,
        },
        userCurrentTimezone
      );

      const nextBirthday = moment.tz(
        {
          year: endYear,
          month: birthMonth - 1,
          day: birthDay,
        },
        userCurrentTimezone
      );

      const end = nextBirthday.clone().subtract(1, 'day').endOf('day');

      return { start, end };
    }

    case PredictionType.MONTH:
    case MDADPredictionType.AD:
      {
        if (!targetMonth) {
          throw new Error('Target month is required for MONTH prediction type');
        }

        let monthNumber =
          targetMonth === MonthEnum.CURRENT_MONTH
            ? getMonthAccordingToBirthdayDay(birthDate, userCurrentTimezone, birthTimezone)
            : targetMonth;

        if (!monthNumber) monthNumber = now.month() + 1;

        let targetYear = getTargetYear({ birthDate, birthTZ: birthTimezone, currentTZ: userCurrentTimezone, targetMonth: monthNumber })

        const range = getMonthRange(targetYear, monthNumber, birthDay, userCurrentTimezone)


        return {
          start: range.startUTC,
          end: range.endUTC
        }
      }

    case PredictionType.DAY: {
      if (!targetDay) {
        throw new Error('Target day is required for DAY prediction type');
      }

      let month;

      if (!targetMonth || targetMonth === MonthEnum.CURRENT_MONTH) {
        const currentMonth = getMonthAccordingToBirthdayDay(birthDate, userCurrentTimezone, birthTimezone);
        if (currentMonth) month = currentMonth;
      } else {
        month = targetMonth;
      }

      if (!month) {
        throw new Error('Target day is required for DAY prediction type');
      }

      let targetYear = getTargetYear({ birthDate, birthTZ: birthTimezone, currentTZ: userCurrentTimezone, targetMonth: month, targetDay })

      const start = moment.tz(
        {
          year: targetYear,
          month: month - 1,
          day: targetDay,
          hour: 0,
          minute: 0,
          second: 0,
        },
        userCurrentTimezone
      );

      const end = start.clone().endOf('day');

      return { start, end };
    }

    default:
      throw new Error(`Unknown prediction type: ${predictionType}`);
  }
}

function filterIntervalsByRange(
  intervals,
  range,
  userCurrentTimezone
) {
  const rangeStart = range.start;
  const rangeEnd = range.end;

  return intervals.filter((interval) => {
    // Convert interval dates from UTC to user's current timezone
    const intervalStart = moment
      .utc(interval.start)
      .tz(userCurrentTimezone);

    const intervalEnd = moment
      .utc(interval.end)
      .tz(userCurrentTimezone);

    return intervalStart < rangeEnd && intervalEnd > rangeStart;

  });
}

export function getFilteredIntervals(intervals, params) {
  try {

    const range = getDateRange(params);

    const filteredIntervals = filterIntervalsByRange(intervals, range, params.userCurrentTimezone);

    console.log(range)
    return {
      intervals: filteredIntervals,
      range,
    };
  }
  catch (error) {
    return {
      intervals: [],
      range: null
    }
  }
}